"use client";

import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTranslations } from "next-intl";
import Image from "next/image";

interface SocialAuthButtonsProps {
  onGoogleClick: () => void;
  onFacebookClick: () => void;
  isLoading?: boolean;
  disabled?: boolean;
  className?: string;
  showLabels?: boolean;
}

export default function SocialAuthButtons({
  onGoogleClick,
  onFacebookClick,
  isLoading = false,
  disabled = false,
  className,
  showLabels = true,
}: SocialAuthButtonsProps) {
  const t = useTranslations("general");
  const [loadingProvider, setLoadingProvider] = useState<"google" | "facebook" | null>(null);

  const handleGoogleClick = async () => {
    if (disabled || isLoading) return;
    setLoadingProvider("google");
    try {
      await onGoogleClick();
    } finally {
      setLoadingProvider(null);
    }
  };

  const handleFacebookClick = async () => {
    if (disabled || isLoading) return;
    setLoadingProvider("facebook");
    try {
      await onFacebookClick();
    } finally {
      setLoadingProvider(null);
    }
  };

  // Only show loading spinner on the specific button being clicked
  // isLoading prop is used to disable both buttons, but not to show loading state
  const isGoogleLoading = loadingProvider === "google";
  const isFacebookLoading = loadingProvider === "facebook";
  
  // Disable both buttons if isLoading is true OR if the specific button is loading
  const isGoogleDisabled = disabled || isLoading || isGoogleLoading;
  const isFacebookDisabled = disabled || isLoading || isFacebookLoading;

  return (
    <div className={cn("space-y-3", className)}>
      {/* Google Button */}
      <Button
        type="button"
        onClick={handleGoogleClick}
        disabled={isGoogleDisabled}
        className={cn(
          "w-full rounded-full bg-white hover:bg-gray-100 text-gray-900 font-poppins font-medium py-5 sm:py-6 text-sm sm:text-base transition-colors",
          "flex items-center justify-center gap-3",
          isGoogleDisabled && "opacity-50 cursor-not-allowed"
        )}
      >
        {isGoogleLoading ? (
          <>
            <Loader2 className="h-5 w-5 animate-spin" />
            {showLabels && <span>{t("loading") || "Loading..."}</span>}
          </>
        ) : (
          <>
            <svg
              className="h-5 w-5"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                fill="#4285F4"
              />
              <path
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                fill="#34A853"
              />
              <path
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                fill="#FBBC05"
              />
              <path
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                fill="#EA4335"
              />
            </svg>
            {showLabels && (
              <span>
                {t("continue_with_google") || "Continue with Google"}
              </span>
            )}
          </>
        )}
      </Button>

      {/* Facebook Button */}
      <Button
        type="button"
        onClick={handleFacebookClick}
        disabled={isFacebookDisabled}
        className={cn(
          "w-full rounded-full bg-gradient-to-t from-primary to-second-primary hover:opacity-90 text-white font-poppins font-medium py-5 sm:py-6 text-sm sm:text-base transition-all",
          "flex items-center justify-center gap-3",
          isFacebookDisabled && "opacity-50 cursor-not-allowed"
        )}
      >
        {isFacebookLoading ? (
          <>
            <Loader2 className="h-5 w-5 animate-spin" />
            {showLabels && <span>{t("loading") || "Loading..."}</span>}
          </>
        ) : (
          <>
            <svg
              className="h-5 w-5"
              viewBox="0 0 24 24"
              fill="currentColor"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
            </svg>
            {showLabels && (
              <span>
                {t("continue_with_facebook") || "Continue with Facebook"}
              </span>
            )}
          </>
        )}
      </Button>
    </div>
  );
}

